import React, { ReactNode, useEffect, useId } from 'react';
import classNames from 'classnames';
import { Button } from './Button';
import { Icon } from './Icon';

type ModalStatus = 'info' | 'success' | 'warning' | 'error';

const STATUS_ICON: Record<ModalStatus, string> = {
  info: 'semantic-info',
  success: 'semantic-success',
  warning: 'semantic-error',
  error: 'semantic-error',
};

export interface ModalProps {
  shown: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  onCancel?: () => void;
  title?: ReactNode;
  status?: ModalStatus;
  buttonConfirmText?: ReactNode;
  buttonConfirmLabel?: string;
  buttonCancelText?: ReactNode;
  buttonCancelLabel?: string;
  large?: boolean;
  stretch?: boolean;
  className?: string;
  testId?: string;
  children?: ReactNode;
}

// Always rendered and toggled with aria-hidden, as the Bronson modal was:
// the tests read a closed modal's aria-hidden, and its content stays mounted.
export function Modal({
  shown,
  onClose,
  onConfirm,
  onCancel,
  title,
  status,
  buttonConfirmText,
  buttonConfirmLabel,
  buttonCancelText,
  buttonCancelLabel,
  large,
  stretch,
  className,
  testId,
  children,
}: ModalProps) {
  const titleId = useId();

  useEffect(() => {
    if (!shown) return undefined;
    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [shown, onClose]);

  return (
    <div
      data-testid={testId}
      aria-hidden={!shown}
      className={classNames('ec-modal', large && 'ec-modal--large', stretch && 'ec-modal--stretch', className)}
    >
      <div className="ec-modal__overlay" onClick={onClose} />
      <div className="ec-modal__window" role="dialog" aria-modal="true" aria-labelledby={title ? titleId : undefined}>
        <header className="ec-modal__header">
          {status ? <Icon name={STATUS_ICON[status]} className={`ec-modal__status ec-modal__status--${status}`} /> : null}
          {title ? <h2 id={titleId} className="ec-modal__title">{title}</h2> : null}
          <button type="button" className="ec-modal__close" aria-label="Close" title="Close" onClick={onClose}>
            <Icon name="semantic-close" />
          </button>
        </header>
        <div className="ec-modal__content">{children}</div>
        {buttonConfirmText || buttonCancelText ? (
          <footer className="ec-modal__footer">
            {buttonCancelText ? (
              <Button type="button" secondary onClick={onCancel} ariaLabel={buttonCancelLabel}>{buttonCancelText}</Button>
            ) : null}
            {buttonConfirmText ? (
              <Button type="button" onClick={onConfirm} ariaLabel={buttonConfirmLabel}>{buttonConfirmText}</Button>
            ) : null}
          </footer>
        ) : null}
      </div>
    </div>
  );
}
